import { el } from '../../lib/dom';
import type { AnalyserLike } from '../../ports/audio';
import type { AppState } from '../../store/app';

/**
 * Spektralni vizualizator (P2 §3.9) — **pravi**, iz `AnalyserNode`.
 *
 * ⛔ **Je DEKORACIJA in nič drugega** (CLAUDE.md, pravilo 1). Ne kodira
 * glasnosti, kakovosti ne uspeha; barvo nosi `--nw-spectrum` (brand), nikoli
 * `--success`/`--danger`. Zato `aria-hidden` in nobene besede zraven — kar
 * bralnik zaslona ne more prebrati, tudi ne sme nositi podatka.
 *
 * Stara aplikacija (DS72-Radio) je risala **lažne** sinusoide, ki z zvokom niso
 * imele zveze. Ta je vezan na tok — cena je Web Audio graf in s tem pot prek
 * Workerja (T1 §4), zato je za stikalom in privzeto izklopljen.
 *
 * ---
 *
 * ✅ **D2 §30 rešen v DS v4.20.7** (`9361267`). Komponenta zdaj nosi oboje sama:
 * `.visualizer > i` ima `align-self: stretch` (prej je bila palica visoka
 * **0px** in `scaleY()` ni imel česa raztegniti), `.visualizer.live > i` pa
 * `animation: none` za podatkovni način.
 *
 * Zato je tu **razred `live`, ne dve vrstici inline sloga**. Aplikacija nastavi
 * samo `transform: scaleY(…)`; kdaj palica animira sama in kdaj ne, ve DS.
 */

/**
 * ⛔ **Število palic je odvisno od širine in ni konstanta.**
 *
 * DS `.visualizer > i` je `flex: 1` — palice si širino **razdelijo**. Fiksnih
 * dvajset je na telefonu videti prav, na namizju pa dobi vsaka ~90px in
 * `border-radius: var(--radius-full)` iz nje naredi elipso: pri 13px višine so
 * to lise, ne spekter. Opaženo na namizju 2026-07-21, po objavi.
 *
 * Edini vzvod, ki ga aplikacija tu ima, je **koliko** palic ustvari — kako jih
 * razporedi, je stvar komponente. Zato korak, ne število.
 */
const BAR_PITCH_PX = 10;

/** Spodnja meja: pod tem ni več spekter, ampak nekaj stolpcev. */
const MIN_BARS = 12;

/**
 * Delež spektra, ki ga sploh pokažemo.
 *
 * Zgornja tretjina predalov pri glasbi tako rekoč vedno miruje — pri 44,1 kHz
 * je zadnji predal okoli 22 kHz. Brez odreza bi bila desna tretjina
 * vizualizatorja stalno pri tleh in videti kot napaka.
 */
const SPECTRUM_SHARE = 0.66;

/** Najnižja višina palice: pri nič je videti kot manjkajoč element, ne kot tišina. */
const FLOOR = 0.06;

export interface VisualizerDeps {
  /** `null`, kadar Web Audio grafa ni — takrat ni česa risati. */
  getAnalyser(): AnalyserLike | null;
}

export interface Visualizer {
  update(state: AppState): void;
  /** Ustavi zanko; za teste in razgradnjo. */
  stop(): void;
}

export function createVisualizer(root: HTMLElement, deps: VisualizerDeps): Visualizer {
  const element = el('div', { class: 'visualizer nw-visualizer', 'aria-hidden': 'true' });
  element.toggleAttribute('hidden', true);
  root.append(element);

  let bars: HTMLElement[] = [];

  /**
   * Zgornja meja je **fizikalna, ne oblikovalska**: več palic, kot je
   * uporabnih frekvenčnih predalov, pomeni sosede z istim odčitkom. Analizator
   * ob prvem okvirju pove, koliko jih ima; do takrat velja skromna ocena.
   */
  function maxBars(): number {
    const analyser = deps.getAnalyser();
    if (analyser === null) return 64;
    return Math.max(MIN_BARS, Math.floor(analyser.frequencyBinCount * SPECTRUM_SHARE));
  }

  /**
   * Palice se **prezidajo ob spremembi širine**, ne ob vsakem okvirju.
   *
   * Meri se `root` (medijska vrstica) in ne sam vizualizator: ta je pogosto
   * `hidden` in bi vrnil širino nič — takrat bi na vsakem vklopu nastalo
   * `MIN_BARS` palic ne glede na zaslon.
   */
  function fitBars(): void {
    const width = root.clientWidth;
    if (width === 0) return;

    const wanted = Math.min(maxBars(), Math.max(MIN_BARS, Math.floor(width / BAR_PITCH_PX)));
    if (wanted === bars.length) return;

    bars = Array.from({ length: wanted }, () => el('i'));
    levels = new Array<number>(wanted).fill(-1);
    element.replaceChildren(...bars);
  }

  /**
   * `prefers-reduced-motion` se med sejo spremeni (sistemska nastavitev), zato
   * poizvedba ni prebrana enkrat, ampak poslušana (R3 §4).
   *
   * Ograjeno s `try`: enotni testi tečejo v okolju brez `matchMedia`.
   */
  let reduced = false;
  try {
    const query = matchMedia('(prefers-reduced-motion: reduce)');
    reduced = query.matches;
    query.addEventListener('change', (event) => {
      reduced = event.matches;
      if (reduced) stopLoop();
    });
  } catch {
    reduced = false;
  }

  let raf: number | null = null;
  let data: Uint8Array | null = null;
  /**
   * Zadnja zapisana višina vsake palice.
   *
   * Na širokem zaslonu jih je krepko čez sto in večina se med okvirjema ne
   * premakne opazno. Primerjava prihrani zapis v slog, ki bi sprožil ponovni
   * izris — okras ne sme stati toliko kot vsebina.
   */
  let levels: number[] = [];

  try {
    new ResizeObserver(() => {
      fitBars();
    }).observe(root);
  } catch {
    // Okolje brez ResizeObserver (enotni testi): palice ostanejo, kot so bile.
  }

  function stopLoop(): void {
    if (raf === null) return;
    cancelAnimationFrame(raf);
    raf = null;
    /**
     * Inline `transform` se **pospravi**, ne zamrzne.
     *
     * Dokler stoji, premaga tudi pravilo DS za `prefers-reduced-motion`
     * (`transform: scaleY(0.55)`) — inline je v kaskadi močnejši od
     * stylesheeta. Ustavljena zanka bi tako pustila palice na zadnji izmerjeni
     * višini, kar je videti kot obvisel vmesnik.
     *
     * Razred `live` odpade skupaj z njim: brez zanke naj komponenta spet
     * animira sama, kot je zamišljena (okrasni način).
     */
    element.classList.remove('live');
    for (const bar of bars) bar.style.removeProperty('transform');
    levels.fill(-1);
  }

  function frame(): void {
    raf = requestAnimationFrame(frame);
    const analyser = deps.getAnalyser();
    if (analyser === null || bars.length === 0) return;

    const bins = analyser.frequencyBinCount;
    if (data === null || data.length !== bins) data = new Uint8Array(bins);
    analyser.getByteFrequencyData(data);

    const usable = Math.max(bars.length, Math.floor(bins * SPECTRUM_SHARE));
    const perBar = usable / bars.length;

    bars.forEach((bar, index) => {
      const from = Math.floor(index * perBar);
      const to = Math.max(from + 1, Math.floor((index + 1) * perBar));
      let sum = 0;
      for (let bin = from; bin < to; bin += 1) sum += data?.[bin] ?? 0;
      const level = Math.max(FLOOR, sum / (to - from) / 255);

      // Dve decimalki sta meja vidnega pri 84px visoki vrstici; pod njo je
      // zapis v slog čista poraba.
      const rounded = Math.round(level * 100) / 100;
      if (rounded === levels[index]) return;
      levels[index] = rounded;
      bar.style.transform = `scaleY(${rounded})`;
    });
  }

  function startLoop(): void {
    if (raf !== null) return;
    // Zadnja priložnost, da število palic ustreza zaslonu: ob prvem vklopu
    // `getAnalyser()` končno ni `null` in `maxBars()` pove pravo zgornjo mejo.
    fitBars();
    // Podatkovni način DS (v4.20.7): `.visualizer.live > i` umakne lastno
    // animacijo. Brez razreda bi ta v kaskadi premagala inline `transform`
    // spodaj in palice bi utripale po svoje, ne po zvoku.
    element.classList.add('live');
    raf = requestAnimationFrame(frame);
  }

  fitBars();

  return {
    update(state) {
      /**
       * Zanka teče samo, kadar je res kaj za risati:
       *   · uporabnik je vizualizator vklopil,
       *   · zvok igra (ob pavzi analizator meri tišino),
       *   · graf obstaja — brez njega `getAnalyser()` vrne `null`,
       *   · in gibanje ni odsvetovano.
       *
       * `requestAnimationFrame` brskalnik v ozadju sam ustavi, zato tu ni
       * dodatnega preverjanja vidnosti.
       */
      const live =
        state.settings.visualizerEnabled &&
        state.playback === 'playing' &&
        !reduced &&
        deps.getAnalyser() !== null;

      element.toggleAttribute('hidden', !live);
      if (live) startLoop();
      else stopLoop();
    },

    stop: stopLoop,
  };
}
